// ============================================================
//  AVIRAAPRO ERP — Offline Queue Manager
//  File: src/utils/offlineQueue.ts
//  ✅ Saves failed POST/PUT/DELETE to localStorage when offline
//  ✅ Auto-syncs all queued requests when internet returns
//  ✅ Shows toast notifications on sync
// ============================================================

export interface QueuedRequest {
  id: string;
  timestamp: number;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  data?: any;
  headers?: Record<string, string>;
  label?: string;      // Human-readable label e.g. "Sales Order #1234"
  retries: number;
  maxRetries: number;
}

const QUEUE_KEY = 'zentrix_offline_queue';
const MAX_RETRIES = 3;

// ── Queue Operations ──────────────────────────────────────────

export function getQueue(): QueuedRequest[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: QueuedRequest[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('[OfflineQueue] Failed to save queue to localStorage:', e);
  }
}

export function enqueue(request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retries' | 'maxRetries'>): string {
  const id = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const item: QueuedRequest = {
    ...request,
    id,
    timestamp: Date.now(),
    retries: 0,
    maxRetries: MAX_RETRIES,
  };

  const queue = getQueue();
  queue.push(item);
  saveQueue(queue);

  console.log(`[OfflineQueue] Queued ${request.method} ${request.url} (${queue.length} total pending)`);
  return id;
}

export function dequeue(id: string): void {
  const queue = getQueue().filter(r => r.id !== id);
  saveQueue(queue);
}

export function clearQueue(): void {
  localStorage.removeItem(QUEUE_KEY);
}

export function getQueueCount(): number {
  return getQueue().length;
}

// ── Sync Engine ────────────────────────────────────────────────

type SyncCallback = (result: { synced: number; failed: number; total: number }) => void;

let _syncInProgress = false;

export async function syncQueue(onComplete?: SyncCallback): Promise<void> {
  if (_syncInProgress) return;

  const queue = getQueue();
  if (queue.length === 0) return;

  _syncInProgress = true;
  console.log(`[OfflineQueue] Starting sync of ${queue.length} queued requests...`);

  let synced = 0;
  let failed = 0;

  for (const req of queue) {
    try {
      // Dynamically import axios to avoid circular dependency
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('token') || localStorage.getItem('erp_token');
      const tenantId = localStorage.getItem('tenant_id') || localStorage.getItem('erp_tenant_id');
      const companyId = localStorage.getItem('company_id') || localStorage.getItem('erp_company_id');

      await axios({
        method: req.method,
        url: req.url,
        data: req.data,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
          ...(companyId ? { 'X-Company-ID': companyId } : {}),
          ...req.headers,
        },
        timeout: 15000,
      });

      dequeue(req.id);
      synced++;
      console.log(`[OfflineQueue] ✅ Synced: ${req.method} ${req.url}`);

    } catch (error: any) {
      const isNetworkError = !error.response;

      if (isNetworkError) {
        // Still offline — stop trying
        console.log('[OfflineQueue] Still offline, stopping sync');
        break;
      }

      // Server returned an error (4xx/5xx) — increment retries
      const updated = getQueue().map(r =>
        r.id === req.id ? { ...r, retries: r.retries + 1 } : r
      );

      // If max retries exceeded, remove from queue
      const item = updated.find(r => r.id === req.id);
      if (item && item.retries >= item.maxRetries) {
        console.warn(`[OfflineQueue] ❌ Max retries exceeded for ${req.url}, removing from queue`);
        dequeue(req.id);
      } else {
        saveQueue(updated);
      }

      failed++;
    }
  }

  _syncInProgress = false;

  const remaining = getQueueCount();
  console.log(`[OfflineQueue] Sync complete — synced: ${synced}, failed: ${failed}, remaining: ${remaining}`);

  if (onComplete) {
    onComplete({ synced, failed, total: synced + failed });
  }
}
